import { useEffect, useState } from "react";
import { API, api, formatApiError, ksh } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { AlertTriangle, BarChart3, CheckCircle2, Clock, Download, Loader2, Package, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { devLog } from "@/lib/devLog";

export default function StatsTab({ onGoToIntegrations }) {
  const [stats, setStats] = useState(null);     // legacy /admin/stats (basic counts)
  const [v2, setV2] = useState(null);            // filtered v2
  const [leaders, setLeaders] = useState([]);
  const [period, setPeriod] = useState("week");
  const [loadingLb, setLoadingLb] = useState(true);
  const [loadingV2, setLoadingV2] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [healthSnap, setHealthSnap] = useState(null);
  const [refreshingHealth, setRefreshingHealth] = useState(false);

  const loadHealth = async () => {
    try {
      const { data } = await api.get("/admin/integrations/diagnostics/summary");
      setHealthSnap(data);
    } catch (err) {
      devLog.debug("integration health fetch failed", err);
    }
  };

  const refreshHealth = async () => {
    setRefreshingHealth(true);
    try {
      const { data } = await api.post("/admin/integrations/diagnostics/refresh");
      setHealthSnap({ ...data, available: true });
      toast.success(data.all_ok ? "All integrations healthy" : "Rechecked — see banner");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshingHealth(false);
    }
  };

  useEffect(() => { loadHealth(); }, []);

  // Filter state
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(today);
  const [staffId, setStaffId] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("all");
  const [paidOnly, setPaidOnly] = useState(true);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => {
    // Analytics summary — tutorial clicks + live users right now.
    api.get("/analytics/admin/summary").then((r) => setAnalytics(r.data)).catch(() => {});
    const t = setInterval(() => {
      api.get("/analytics/admin/summary").then((r) => setAnalytics(r.data)).catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    api.get("/admin/staff/list").then((r) => setStaffOptions(r.data.staff || [])).catch(() => {});
  }, []);

  const fetchV2 = async () => {
    setLoadingV2(true);
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", `${dateFrom}T00:00:00+00:00`);
    if (dateTo) params.append("date_to", `${dateTo}T23:59:59+00:00`);
    if (staffId) params.append("staff_id", staffId);
    if (deliveryMethod && deliveryMethod !== "all") params.append("delivery_method", deliveryMethod);
    params.append("paid_only", String(paidOnly));
    try {
      const { data } = await api.get(`/admin/stats/v2?${params.toString()}`);
      setV2(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load stats");
    } finally {
      setLoadingV2(false);
    }
  };

  useEffect(() => { fetchV2(); }, [dateFrom, dateTo, staffId, deliveryMethod, paidOnly]);

  useEffect(() => {
    setLoadingLb(true);
    api.get(`/admin/stats/leaderboard?period=${period}`)
      .then((r) => setLeaders(r.data.leaders || []))
      .finally(() => setLoadingLb(false));
  }, [period]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append("date_from", `${dateFrom}T00:00:00+00:00`);
      if (dateTo) params.append("date_to", `${dateTo}T23:59:59+00:00`);
      if (staffId) params.append("staff_id", staffId);
      if (deliveryMethod && deliveryMethod !== "all") params.append("delivery_method", deliveryMethod);
      params.append("paid_only", String(paidOnly));
      // Use raw fetch so we can ensure the response is treated as a Blob
      // regardless of axios interceptor config.
      const token = localStorage.getItem("ctp_token");
      const res = await fetch(`${API}/admin/stats/report.pdf?${params.toString()}`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ctp-stats-${dateFrom || "all"}_to_${dateTo || "now"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick — some browsers race the click handler
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(formatApiError(e?.message) || "Could not download report");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!stats) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;
  const cards = [
    { label: "Total Orders", val: stats.total_orders, icon: Package, testId: ADMIN.statTotal },
    { label: "Revenue", val: ksh(stats.revenue), icon: BarChart3, testId: ADMIN.statRevenue },
    { label: "Pending Payment", val: stats.pending_orders, icon: Clock },
    { label: "Printing", val: stats.printing_orders, icon: Package },
    { label: "Ready", val: stats.ready_orders, icon: CheckCircle2 },
    { label: "Completed", val: stats.completed_orders, icon: CheckCircle2 },
  ];

  const fmtMin = (m) => {
    if (m == null) return "—";
    if (m < 60) return `${Math.round(m)}m`;
    const h = Math.floor(m / 60);
    return `${h}h ${Math.round(m - h * 60)}m`;
  };

  const breakdownSum = Math.max(v2?.earnings_breakdown_sum || 0, 1);

  return (
    <div className="space-y-8">
      {/* Integration health banner (auto-updated on backend startup + daily) */}
      <IntegrationHealthBanner
        snap={healthSnap}
        refreshing={refreshingHealth}
        onRefresh={refreshHealth}
        onGoToIntegrations={onGoToIntegrations}
      />

      {/* Realtime & marketing analytics */}
      {analytics && (
        <div className="grid sm:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]" data-testid="analytics-cards">
          <div className="bg-white p-6" data-testid="analytics-live-users">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">Live users right now</div>
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${analytics.live_users_now > 0 ? "bg-[#059669]" : "bg-[#B0B0B0]"} opacity-75`}></span>
                <span className={`relative inline-flex rounded-full h-3 w-3 ${analytics.live_users_now > 0 ? "bg-[#059669]" : "bg-[#B0B0B0]"}`}></span>
              </span>
              <span className="font-display font-black text-4xl">{analytics.live_users_now}</span>
            </div>
            <div className="mono text-[10px] text-[#525252] mt-2">
              Anonymous sessions active in the last 2 min. Refreshes every 30 s.
            </div>
          </div>
          <div className="bg-white p-6" data-testid="analytics-tutorial-clicks">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252] mb-3">Tutorial button clicks</div>
            <div className="font-display font-black text-4xl">{analytics.tutorial_click_total.toLocaleString()}</div>
            <div className="mono text-[10px] text-[#525252] mt-2">
              {analytics.tutorial_last_click_at
                ? `Last click ${new Date(analytics.tutorial_last_click_at).toLocaleString("en-KE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Africa/Nairobi" })} EAT`
                : "No clicks yet — add a tutorial URL to enable"}
            </div>
          </div>
        </div>
      )}

      {/* All-time KPI cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
        {cards.map((c) => (
          <div key={c.label} className="bg-white p-6" data-testid={c.testId}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">{c.label}</div>
              <c.icon className="w-5 h-5 text-[#525252]" />
            </div>
            <div className="font-display font-black text-4xl">{c.val}</div>
          </div>
        ))}
      </div>

      {/* Filtered analysis with PDF download */}
      <div className="bg-white border border-[#E5E5E5] p-5" data-testid="stats-filters">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Drill-down</div>
            <h2 className="font-display font-black text-2xl">Filtered analysis</h2>
          </div>
          <button
            onClick={downloadPdf}
            disabled={downloadingPdf || loadingV2}
            data-testid="stats-download-pdf"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold hover:bg-[#059669] disabled:opacity-50"
          >
            {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            Download PDF
          </button>
        </div>

        <div className="grid md:grid-cols-5 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="stats-date-from" className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="stats-date-to" className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-sm" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Staff</label>
            <select value={staffId} onChange={(e) => setStaffId(e.target.value)} data-testid="stats-staff" className="w-full px-3 py-2 border border-[#E5E5E5] outline-none text-sm">
              <option value="">All staff</option>
              {staffOptions.map((s) => <option key={s.id} value={s.id}>{s.name || s.email} ({s.role})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Delivery</label>
            <select value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)} data-testid="stats-delivery" className="w-full px-3 py-2 border border-[#E5E5E5] outline-none text-sm">
              <option value="all">All</option>
              <option value="pickup">Pickup</option>
              <option value="nairobi_delivery">Nairobi Delivery</option>
              <option value="countrywide">Countrywide</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-xs text-[#525252] pb-2">
              <input type="checkbox" checked={paidOnly} onChange={(e) => setPaidOnly(e.target.checked)} data-testid="stats-paid-only" className="w-4 h-4" />
              Paid only
            </label>
          </div>
        </div>

        {loadingV2 ? (
          <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#059669]" /></div>
        ) : v2 && (
          <div className="mt-5 space-y-5" data-testid="stats-v2">
            {/* KPIs row */}
            <div className="grid sm:grid-cols-4 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
              {[
                { l: "Orders", v: v2.orders_count?.toLocaleString() || "0" },
                { l: "Revenue", v: ksh(v2.revenue) },
                { l: "Avg order", v: ksh(v2.average_order_value) },
                { l: "Pages printed", v: v2.pages_total?.toLocaleString() || "0" },
              ].map((c) => (
                <div key={c.l} className="bg-white p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">{c.l}</div>
                  <div className="font-display font-black text-2xl mt-1">{c.v}</div>
                </div>
              ))}
            </div>

            {/* Earnings breakdown */}
            <div className="border border-[#E5E5E5]" data-testid="stats-breakdown">
              <div className="px-4 py-2 bg-[#0A0A0A] text-white text-[10px] uppercase tracking-[0.2em] font-bold">
                Earnings by category
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Category</th>
                    <th className="text-right p-3 uppercase tracking-[0.15em] text-[10px]">Amount</th>
                    <th className="text-right p-3 uppercase tracking-[0.15em] text-[10px]">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {v2.earnings_breakdown.map((b) => {
                    const pct = breakdownSum ? (b.amount / breakdownSum) * 100 : 0;
                    return (
                      <tr key={b.category} className="border-b border-[#E5E5E5] last:border-b-0" data-testid={`breakdown-row-${b.category.replace(/[^a-z]/gi, "")}`}>
                        <td className="p-3">{b.category}</td>
                        <td className="p-3 text-right mono">{ksh(b.amount)}</td>
                        <td className="p-3 text-right mono">
                          <div className="inline-flex items-center justify-end gap-2">
                            <div className="w-16 h-1 bg-[#E5E5E5] relative overflow-hidden hidden sm:block">
                              <div className="absolute inset-y-0 left-0 bg-[#059669]" style={{ width: `${Math.min(100, pct)}%` }} />
                            </div>
                            <span className="text-[#525252] w-12 text-right">{pct.toFixed(1)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="bg-[#FAFAFA] font-bold">
                    <td className="p-3">Total (excl. discounts)</td>
                    <td className="p-3 text-right mono">{ksh(v2.earnings_breakdown_sum)}</td>
                    <td className="p-3 text-right mono">100.0%</td>
                  </tr>
                  {v2.discount_total > 0 && (
                    <tr className="text-[#059669]">
                      <td className="p-3">Discounts given</td>
                      <td className="p-3 text-right mono">-{ksh(v2.discount_total)}</td>
                      <td className="p-3" />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Staff leaderboard */}
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#525252]">§ Staff Performance</div>
            <h2 className="font-display font-black text-2xl">Leaderboard</h2>
          </div>
          <div className="flex gap-1" data-testid="leaderboard-period">
            {[
              { v: "today", l: "Today" },
              { v: "week", l: "This week" },
              { v: "month", l: "This month" },
              { v: "all", l: "All time" },
            ].map((p) => (
              <button
                key={p.v}
                onClick={() => setPeriod(p.v)}
                data-testid={`leaderboard-${p.v}`}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-bold border ${period === p.v ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] text-[#525252] hover:border-[#0A0A0A]"}`}
              >{p.l}</button>
            ))}
          </div>
        </div>
        <div className="border border-[#E5E5E5] bg-white overflow-x-auto" data-testid="leaderboard-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white">
              <tr>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px] w-12">#</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Staff</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Marked Ready</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Completed</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">Avg time-to-ready</th>
                <th className="text-left p-3 uppercase tracking-[0.15em] text-[10px]">% ready before 5pm</th>
              </tr>
            </thead>
            <tbody>
              {loadingLb ? (
                <tr><td colSpan={6} className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#059669]" /></td></tr>
              ) : leaders.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#525252]">No fulfilment activity in this period yet.</td></tr>
              ) : leaders.map((l, i) => (
                <tr key={l.actor_id} className="border-b border-[#E5E5E5] last:border-b-0 hover:bg-[#F5F5F5]">
                  <td className="p-3 mono font-bold">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</td>
                  <td className="p-3">
                    <div className="font-bold">{l.actor_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[#525252]">{l.actor_role}</div>
                  </td>
                  <td className="p-3 mono font-display font-black text-xl text-[#059669]">{l.ready_count}</td>
                  <td className="p-3 mono font-bold">{l.completed_count}</td>
                  <td className="p-3 mono font-bold">{fmtMin(l.avg_time_to_ready_min)}</td>
                  <td className="p-3 mono font-bold">
                    <span className={l.pct_ready_before_5pm >= 75 ? "text-[#059669]" : l.pct_ready_before_5pm >= 50 ? "text-[#F59E0B]" : "text-[#DC2626]"}>
                      {l.pct_ready_before_5pm}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252] mt-2">
          Sourced from the order audit trail. Time-to-ready measures printing → ready. &ldquo;Before 5pm&rdquo; uses Kenya time (UTC+3).
        </div>
      </div>
    </div>
  );
}

function IntegrationHealthBanner({ snap, refreshing, onRefresh, onGoToIntegrations }) {
  if (!snap || !snap.available) return null;
  const mp = snap.mpesa || {};
  const wa = snap.whatsapp || {};
  const counts = snap.counts || {};
  const errors = (counts.mpesa?.errors || 0) + (counts.whatsapp?.errors || 0);
  const warnings = (counts.mpesa?.warnings || 0) + (counts.whatsapp?.warnings || 0);
  const ok = snap.all_ok && errors === 0 && warnings === 0;

  const ran = snap.ran_at ? new Date(snap.ran_at) : null;
  const rel = ran ? relativeAgo(ran) : "recently";

  const Icon = ok ? ShieldCheck : errors > 0 ? XCircle : AlertTriangle;
  const tone = ok
    ? { border: "border-[#059669]", bg: "bg-[#059669]/10", text: "text-[#059669]" }
    : errors > 0
    ? { border: "border-[#DC2626]", bg: "bg-[#DC2626]/10", text: "text-[#DC2626]" }
    : { border: "border-[#F59E0B]", bg: "bg-[#F59E0B]/10", text: "text-[#92400E]" };

  return (
    <div
      className={`border-2 ${tone.border} ${tone.bg} px-4 py-3 flex flex-wrap items-start gap-3`}
      data-testid="integration-health-banner"
    >
      <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${tone.text}`} />
      <div className="flex-1 min-w-[240px]">
        <div className={`text-xs font-bold uppercase tracking-[0.15em] ${tone.text}`}>
          {ok
            ? "All integrations healthy"
            : errors > 0
            ? `${errors} integration blocker${errors === 1 ? "" : "s"} · ${warnings} warning${warnings === 1 ? "" : "s"}`
            : `${warnings} integration warning${warnings === 1 ? "" : "s"} to review`}
        </div>
        <div className="text-[11px] text-[#525252] mono mt-1 space-y-0.5">
          <div>
            <span className="font-bold">M-Pesa</span> · {mp.summary || "n/a"}
          </div>
          <div>
            <span className="font-bold">WhatsApp</span> · {wa.summary || "n/a"}
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#525252] mt-1.5">
          Auto-checked {rel} · source: {snap.source}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {!ok && onGoToIntegrations && (
          <button
            onClick={onGoToIntegrations}
            data-testid="integration-health-open"
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold bg-[#0A0A0A] text-white hover:bg-[#059669]"
          >
            Open Integrations
          </button>
        )}
        <button
          onClick={onRefresh}
          disabled={refreshing}
          data-testid="integration-health-refresh"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Recheck
        </button>
      </div>
    </div>
  );
}

function relativeAgo(date) {
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

